import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Autocomplete,
	TextField,
} from "@mui/material";

import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

const checklistItemSchema = yup.object().shape({
	name: yup
		.string()
		.trim()
		.required("Item Name is required")
		.max(100, maxLengthMessage(100)),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
	inspectionTypes: yup.array().of(yup.object()).nullable(),
});

const buildDefaults = (initialData, inspectionTypeOptions) => ({
	name: initialData?.name || "",
	description: initialData?.description || "",
	inspectionTypes:
		initialData?.inspectionTypes
			?.map((typeId) =>
				inspectionTypeOptions.find(
					(opt) =>
						opt.value === typeId || opt.value === String(typeId),
				),
			)
			.filter(Boolean) || [],
});

const AddEditChecklistItemDialog = ({
	open,
	onClose,
	onSave,
	initialData = null,
	viewOnly = false,
	inspectionTypeOptions = [],
}) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(checklistItemSchema),
		defaultValues: buildDefaults(initialData, inspectionTypeOptions),
		mode: "onChange",
	});

	useEffect(() => {
		if (open) {
			reset(buildDefaults(initialData, inspectionTypeOptions));
		}
	}, [open, initialData, reset, inspectionTypeOptions]);

	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			name: data.name?.trim() || null,
			description: data.description?.trim() || null,
			inspectionTypes: data.inspectionTypes?.map((t) => t.value) || [],
		};

		onSave(payload);
		reset();
		onClose();
	};

	const dialogTitle = initialData
		? viewOnly
			? `View Checklist Item — ${initialData.name}`
			: `Edit Checklist Item — ${initialData.name}`
		: "Add New Checklist Item";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{ pt: 1 }}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "1fr",
							gap: 2,
						}}
					>
						<Box>
							<TextFieldController
								name="name"
								label="Item Name *"
								control={control}
								errors={errors}
								placeholder="e.g. Thermal Camera"
								fullWidth
							/>
						</Box>
						<Box>
							<TextFieldController
								name="description"
								label="Description / Employee Guidance"
								control={control}
								errors={errors}
								placeholder="What the employee should check..."
								multiline
								rows={3}
								fullWidth
							/>
						</Box>
						<Box>
							<Controller
								name="inspectionTypes"
								control={control}
								render={({ field: { onChange, value } }) => (
									<Autocomplete
										multiple
										options={inspectionTypeOptions}
										value={value || []}
										onChange={(_, newValue) =>
											onChange(newValue)
										}
										getOptionLabel={(option) =>
											option?.label || ""
										}
										isOptionEqualToValue={(option, val) =>
											option?.value === val?.value
										}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Applies to Inspection Types"
												variant="outlined"
												size="small"
												error={
													!!errors?.inspectionTypes
												}
												helperText={
													errors?.inspectionTypes
														?.message
												}
											/>
										)}
										fullWidth
									/>
								)}
							/>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Leave empty to include in every inspection type.
							</Typography>
						</Box>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATCloseButton onClick={onClose} tooltipText="Close" />
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
						disabled={!isValid}
					/>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default AddEditChecklistItemDialog;
